import { ChangeDetectionStrategy, Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { FlagIconComponent } from '@rms/ui/flag-icon';
import { PHONE_INPUT_OPTIONS, PhoneInputOption } from '@rms/ui/forms/phone-input';
import { tap } from 'rxjs';

import { AsyncSelectComponent } from '../async-select/async-select.component';

@Component({
  selector: 'rms-country-code-select',
  standalone: true,
  imports: [AsyncSelectComponent, FlagIconComponent, ReactiveFormsModule],
  templateUrl: './country-code-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryCodeSelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountryCodeSelectComponent implements OnInit, ControlValueAccessor {
  @Input() public placeholder = '';
  @Input() public loading = false;
  @Input() public error: string | null = null;

  protected readonly control = new FormControl<PhoneInputOption | null>(null);
  protected readonly options = PHONE_INPUT_OPTIONS;

  private readonly destroy$ = inject(DestroyRef);

  private onChange?: (value: string | null) => void;
  private onTouched?: () => void;

  public ngOnInit(): void {
    this.control.valueChanges
      .pipe(
        tap((option) => {
          this.onChange?.(option ? `+${option.countryCallingCode}` : null);
          this.onTouched?.();
        }),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe();
  }

  public writeValue(value: string | null): void {
    const option = value
      ? (PHONE_INPUT_OPTIONS.find((opt) => `+${opt.countryCallingCode}` === value || opt.code === value) ?? null)
      : null;

    this.control.setValue(option, { emitEvent: false });
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable({ emitEvent: false }) : this.control.enable({ emitEvent: false });
  }

  protected readonly searchFn = (search: string, options: PhoneInputOption[]): PhoneInputOption[] => {
    const lower = search.toLowerCase();

    return options.filter(
      (opt) => opt.name.toLowerCase().includes(lower) || `+${opt.countryCallingCode}`.includes(search)
    );
  };
}
